import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretLeft } from '@fortawesome/pro-solid-svg-icons/faCaretLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Stack, useMantineTheme } from '@mantine/core';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import type { IObservableArray } from 'mobx';
import { Observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RowRenderProps } from 'react-table';

import type {
  Conversion,
  ICampaignAttribution,
  IGoal,
  Targeting,
  TAttributionModel,
} from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { AvatarV2 as Avatar, ContextMenu, Pill, TableColumnHeader, Text } from '@feathr/components';
import { getIconForAction, moment, useRedirect } from '@feathr/hooks';
import { useStore } from '@feathr/report_components/state';

import CampaignChip from '../../CampaignChip';
import PartnerChip from '../../PartnerChip';
import SegmentChip from '../../SegmentChip';
import TemplateChip from '../../TemplateChip';
import { getProductConfig } from '../ConversionTableCard.config';
import type { TProductModel } from '../ConversionTableCard.types';
import PersonName from '../ConversionTableCardV1/PersonName';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from '../ConversionTableCardV1/ConversionTableColumns.css';

interface IPersonCellProps extends RowRenderProps {
  localUrl?: (url: string) => string;
  original: Conversion;
  t: TFunction;
}

function PersonCell({ localUrl, original, t }: Readonly<IPersonCellProps>): JSX.Element {
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        const { Persons } = useStore();
        if (original.get('person')) {
          const model = Persons.get(original.get('person'));
          if (model && !model.isPending) {
            const person = model.toJS();
            return (
              <div className={styles.container}>
                <Avatar name={person.name} placeholder={person.placeholder} />
                <div className={styles.content}>
                  <div className={styles.name}>
                    <PersonName localUrl={localUrl} person={person} />{' '}
                    {model.isArchived && (
                      <Pill showPrefix={false} variant={'secondary'}>
                        {t('Archived')}
                      </Pill>
                    )}
                  </div>
                  {person.external_id && <span>{person.external_id}</span>}
                </div>
              </div>
            );
          }
        }
        return <div className={styles.container}>Anonymous</div>;
      }}
    </Observer>
  );
}

export const defaultColumnIds = [
  'd_c',
  'person',
  'goals',
  'category',
  'value',
  'window',
  'timeline',
];

export function ConversionAnalyticsColumns(
  goals: IGoal[],
  model: TProductModel,
  targetings: IObservableArray<Targeting>,
  attributionModel: TAttributionModel,
  localUrl?: (url: string) => string,
): Array<IColumn<Conversion>> {
  const { t } = useTranslation();

  const columns: Array<IColumn<Conversion>> = [
    {
      Header: TableColumnHeader({ title: t('Conversion Date') }),
      id: 'd_c',
      checkboxLabel: t('Conversion Date'),
      width: 150,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        const dateValue = original.get('d_c');
        const formattedDate = moment.utc(dateValue, moment.ISO_8601).utc().format('MMMM D, YYYY');
        return (
          <Text.DefaultHeadingMedium truncate={true}>{formattedDate}</Text.DefaultHeadingMedium>
        );
      },
    },
    {
      id: 'person',
      Header: TableColumnHeader({ title: t('Name') }),
      checkboxLabel: t('Name'),
      sortable: false,
      className: classNames(tableStyles.cellLeft, styles.person),
      Cell(row): JSX.Element {
        return <PersonCell {...row} localUrl={localUrl} t={t} />;
      },
    },
    {
      id: 'goals',
      Header: TableColumnHeader({ title: t('Goal Name') }),
      checkboxLabel: t('Goal Name'),
      sortable: false,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        const conversionGoalIds = original.get('goals', []) as string[];
        const conversionGoals = goals.filter((g) => conversionGoalIds.includes(g.id));
        if (conversionGoals.length > 0) {
          return <>{conversionGoals.map((g) => g.name).join(', ')}</>;
        }
        return <>-</>;
      },
    },
    {
      id: 'category',
      Header: TableColumnHeader({ title: t('Category') }),
      checkboxLabel: t('Category'),
      sortable: true,
      width: 160,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        return (
          <Text.DefaultRegular truncate={true}>{original.get('category')}</Text.DefaultRegular>
        );
      },
    },
    {
      id: 'campaigns',
      Header: TableColumnHeader({ title: t('Campaigns') }),
      checkboxLabel: t('Campaigns'),
      width: 150,
      sortable: false,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const campaignChips = original
                .get('attribution')
                [
                  attributionModel
                ]?.map((campaignAttribution: ICampaignAttribution) => <CampaignChip id={campaignAttribution.campaign} key={campaignAttribution.campaign} localUrl={localUrl} truncate={true} />);
              if ((campaignChips?.length || 0) > 0) {
                return <Stack gap={4}>{campaignChips}</Stack>;
              }
              return <>-</>;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'source_target_segments',
      Header: TableColumnHeader({ title: t('Source') }),
      checkboxLabel: t('Source'),
      width: 150,
      sortable: false,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const includedTargetSegments = targetings
                .filter((targeting) => targeting.get('included') === true)
                .map((targeting) => targeting.get('target_data'));
              const segmentChips = original
                .get('source_target_segments')
                .filter((segmentId: string) => includedTargetSegments.includes(segmentId))
                .map((segmentId: string) => (
                  <SegmentChip id={segmentId} key={segmentId} localUrl={localUrl} />
                ));
              if (segmentChips.length > 0) {
                return <>{segmentChips}</>;
              }
              return <>-</>;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'partner',
      Header: TableColumnHeader({ title: t('Partners') }),
      checkboxLabel: t('Partners'),
      sortable: false,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        const partnerIds = original.get('objects_touched').p_id;
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              return (
                <>
                  {partnerIds
                    ? partnerIds.map((partnerId: string) => (
                        <PartnerChip id={partnerId} key={partnerId} localUrl={localUrl} />
                      ))
                    : '-'}
                </>
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'template',
      Header: TableColumnHeader({ title: t('Templates') }),
      checkboxLabel: t('Templates'),
      sortable: false,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        const templateIds = original.get('objects_touched').t_id;
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              return (
                <>
                  {templateIds
                    ? templateIds.map((templateId: string) => (
                        <TemplateChip id={templateId} key={templateId} localUrl={localUrl} />
                      ))
                    : '-'}
                </>
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'value',
      Header: TableColumnHeader({ title: t('Value') }),
      checkboxLabel: t('Value'),
      sortable: false,
      width: 100,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        const value = original.get('computed_value');
        return (
          <Text.DefaultRegular truncate={true}>
            {numeral(value).format('$0,0.00')}
          </Text.DefaultRegular>
        );
      },
    },
    {
      Header: TableColumnHeader({ title: t('Timeframe') }),
      id: 'window',
      checkboxLabel: t('Timeframe'),
      sortable: false,
      width: 100,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        const startTS = original.get('date_enter_funnel');
        const endTS = original.get('d_c');
        const start = moment.utc(startTS, moment.ISO_8601);
        const end = moment.utc(endTS, moment.ISO_8601);
        return (
          <Text.DefaultRegular truncate={true}>
            {moment.duration(start.diff(end)).humanize()}
          </Text.DefaultRegular>
        );
      },
    },
    {
      Header: TableColumnHeader({ title: t('Timeline') }),
      id: 'timeline',
      checkboxLabel: t('Timeline'),
      sortable: false,
      expander: true,
      width: 80,
      className: classNames(tableStyles.cellCenter, styles.timeline),
      Expander({ isExpanded }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const {
                spacing: { '2': gap },
              } = useMantineTheme();
              return (
                <Flex align={'center'} gap={gap}>
                  {isExpanded ? (
                    <>
                      <Text.DefaultRegular>{t('Hide')}</Text.DefaultRegular>
                      <FontAwesomeIcon icon={faCaretDown} />
                    </>
                  ) : (
                    <>
                      <Text.DefaultRegular>{t('Show')}</Text.DefaultRegular>
                      <FontAwesomeIcon icon={faCaretLeft} />
                    </>
                  )}
                </Flex>
              );
            }}
          </Observer>
        );
      },
    },
    {
      Header: TableColumnHeader({ title: t('Details') }),
      id: 'details',
      checkboxLabel: t('Details'),
      sortable: false,
      width: 80,
      className: tableStyles.cellCenter,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const {
                spacing: { '2': gap },
              } = useMantineTheme();
              const [, setRedirect] = useRedirect();
              const conversionId = original.id;
              // campaignId will always exist because a conversion can only exist if it's associated with a campaign
              const campaignId = original.get('campaigns')[0];
              const config = getProductConfig(model);
              const link = config.getDetailsUrl(model, campaignId, conversionId);
              return (
                <ContextMenu buttonType={'icon'} position={'left-start'}>
                  <ContextMenu.Item
                    link={localUrl ? setRedirect(localUrl(link)) : ''}
                    target={'_blank'}
                  >
                    <Flex align={'center'} gap={gap}>
                      {t('View details')}
                      {getIconForAction('preview')}
                    </Flex>
                  </ContextMenu.Item>
                </ContextMenu>
              );
            }}
          </Observer>
        );
      },
    },
  ];

  return columns;
}
